import React, { useEffect, useState } from 'react';
import { Calendar, Clock, MapPin, Users, CreditCard, User } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

interface TripData {
  route_name?: string;
  trip_date?: string;
  trip_time?: string;
  group_size?: number;
  language?: string;
  price_per_person?: number;
  payment_status?: string;
  guide_name?: string;
  guide_phone?: string;
}

export const BookingSummaryWidget: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [trip, setTrip] = useState<TripData | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/wycieczki/application/${applicationId}/trips`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((r) => {
      if (r.data?.trips?.length > 0) setTrip(r.data.trips[0]);
    }).catch(() => {});
  }, [applicationId]);

  if (!trip) return null;

  const tripDate = trip.trip_date ? new Date(trip.trip_date) : null;
  const now = new Date();
  const daysUntil = tripDate ? Math.ceil((tripDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;

  const PAYMENT_COLORS: Record<string, string> = {
    paid: 'bg-green-100 text-green-700',
    partial: 'bg-yellow-100 text-yellow-700',
    pending: 'bg-red-100 text-red-700',
  };

  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg shadow p-5 border border-teal-200">
      <h3 className="font-bold text-sm flex items-center gap-2 mb-3 text-teal-800">
        <Calendar size={16} />
        Podsumowanie rezerwacji
        {daysUntil !== null && daysUntil > 0 && (
          <span className="ml-auto text-xs bg-teal-200 text-teal-800 px-2 py-0.5 rounded-full">za {daysUntil} dni</span>
        )}
        {daysUntil !== null && daysUntil <= 0 && daysUntil >= -1 && (
          <span className="ml-auto text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">Dzisiaj!</span>
        )}
      </h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {trip.route_name && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-teal-500 shrink-0" />
            <span className="truncate">{trip.route_name}</span>
          </div>
        )}
        {tripDate && (
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-teal-500 shrink-0" />
            <span>{tripDate.toLocaleDateString('pl-PL')}</span>
          </div>
        )}
        {trip.trip_time && (
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-teal-500 shrink-0" />
            <span>{trip.trip_time}</span>
          </div>
        )}
        {trip.group_size && (
          <div className="flex items-center gap-2">
            <Users size={14} className="text-teal-500 shrink-0" />
            <span>{trip.group_size} os.</span>
          </div>
        )}
        {trip.guide_name && (
          <div className="flex items-center gap-2 col-span-2">
            <User size={14} className="text-teal-500 shrink-0" />
            <span>Przewodnik: {trip.guide_name}</span>
          </div>
        )}
        {trip.payment_status && (
          <div className="flex items-center gap-2 col-span-2">
            <CreditCard size={14} className="text-teal-500 shrink-0" />
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${PAYMENT_COLORS[trip.payment_status] || 'bg-gray-100 text-gray-600'}`}>
              {trip.payment_status === 'paid' ? 'Opłacone' : trip.payment_status === 'partial' ? 'Częściowo' : 'Do zapłaty'}
            </span>
            {trip.price_per_person && <span className="text-gray-500">{trip.price_per_person} zł/os.</span>}
          </div>
        )}
      </div>
    </div>
  );
};
